import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import { LayoutDashboard, Car, History, Euro, MoreHorizontal, BarChart3, Fuel, Building2, Truck, Settings, Database, Plug, Users, Wallet, CreditCard, LogOut, ArrowLeftRight, FileText, ClipboardList, Droplets, CalendarCheck2 } from 'lucide-react';
import { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import NotificationBell from './NotificationBell';
import LanguageSelector from './LanguageSelector';

export default function MobileBottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [moreOpen, setMoreOpen] = useState(false);

  const isAdminDriver = user?.role === 'admin-driver';

  const pathToSection = {
    '/admin': 'dashboard', '/admin/rides': 'rides', '/admin/past-rides': 'past_rides',
    '/admin/users': 'users', '/admin/attendance': 'attendance', '/admin/partners': 'partners', '/admin/vehicles': 'vehicles',
    '/admin/expenses': 'expenses', '/admin/petrol': 'petrol', '/admin/car-wash': 'car_wash', '/admin/financials': 'financials',
    '/admin/debts': 'debts', '/admin/daily-vouchers': 'daily_vouchers', '/admin/statistics': 'statistics',
    '/admin/partner-reports': 'partner_reports', '/admin/backup': 'backup',
    '/admin/integrations': 'integrations', '/admin/settings': 'settings'
  };

  const permissions = user?.section_permissions || {};
  const isSuperAdmin = user?.is_super_admin;
  const canAccess = (path) => {
    if (isSuperAdmin) return true;
    const key = pathToSection[path];
    return key ? permissions[key] !== false : true;
  };

  // Primary navigation items (shown in bottom bar)
  const primaryItems = [
    { path: '/admin', label: 'Home', icon: LayoutDashboard },
    { path: '/admin/rides', label: 'Rides', icon: Car },
    { path: '/admin/past-rides', label: 'Past', icon: History },
    { path: '/admin/expenses', label: 'Expenses', icon: Euro },
  ].filter(item => canAccess(item.path));

  // Secondary items (shown in "More" menu)
  const secondaryItems = [
    { path: '/admin/users', label: 'Users', icon: Users },
    { path: '/admin/attendance', label: 'Attendance', icon: CalendarCheck2 },
    { path: '/admin/partners', label: 'Partners', icon: Building2 },
    { path: '/admin/vehicles', label: 'Vehicles', icon: Truck },
    { path: '/admin/petrol', label: 'Petrol', icon: Fuel },
    { path: '/admin/car-wash', label: 'Car Wash', icon: Droplets },
    { path: '/admin/financials', label: 'Financials', icon: Wallet },
    { path: '/admin/debts', label: 'Debts', icon: CreditCard },
    { path: '/admin/daily-vouchers', label: 'Vouchers', icon: FileText },
    { path: '/admin/statistics', label: 'Statistics', icon: BarChart3 },
    { path: '/admin/partner-reports', label: 'Reports', icon: ClipboardList },
    { path: '/admin/backup', label: 'Backup', icon: Database },
    { path: '/admin/integrations', label: 'Integrations', icon: Plug },
    { path: '/admin/settings', label: 'Settings', icon: Settings },
  ].filter(item => canAccess(item.path));

  const isActive = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin' || location.pathname === '/admin/';
    }
    return location.pathname.startsWith(path);
  };

  const isMoreActive = () => {
    return secondaryItems.some(item => isActive(item.path));
  };

  const handleNavigation = (path) => {
    navigate(path);
    setMoreOpen(false);
  };

  return (
    <>
      {/* Bottom Navigation Bar - Only visible on mobile */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 safe-area-bottom">
        <div className="flex items-center justify-around h-16">
          {primaryItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <button
                key={item.path}
                onClick={() => handleNavigation(item.path)}
                className={`flex flex-col items-center justify-center flex-1 h-full px-2 transition-colors ${
                  active
                    ? 'text-sky-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
              >
                <Icon className={`h-6 w-6 ${active ? 'stroke-[2.5px]' : ''}`} />
                <span className={`text-xs mt-1 ${active ? 'font-semibold' : 'font-medium'}`}>
                  {item.label}
                </span>
              </button>
            );
          })}

          {/* More Menu Trigger */}
          <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
            <SheetTrigger asChild>
              <button
                className={`flex flex-col items-center justify-center flex-1 h-full px-2 transition-colors ${
                  isMoreActive()
                    ? 'text-sky-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid="mobile-nav-more"
              >
                <MoreHorizontal className={`h-6 w-6 ${isMoreActive() ? 'stroke-[2.5px]' : ''}`} />
                <span className={`text-xs mt-1 ${isMoreActive() ? 'font-semibold' : 'font-medium'}`}>
                  More
                </span>
              </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="h-[70vh] rounded-t-3xl flex flex-col !p-0">
              <SheetHeader className="p-6 pb-2 flex-shrink-0">
                <SheetTitle className="text-left">Menu</SheetTitle>
              </SheetHeader>
              
              {/* Scrollable content area */}
              <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-24" data-testid="mobile-menu-scroll">
                {/* User Info */}
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl mb-4">
                  <div className="w-12 h-12 rounded-full bg-sky-100 flex items-center justify-center">
                    <span className="text-sky-600 font-semibold text-lg">
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 truncate">{user?.name}</p>
                    <p className="text-sm text-gray-500 truncate">{user?.email}</p>
                    {isAdminDriver && (
                      <span className="inline-block mt-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">
                        Admin-Driver
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <NotificationBell />
                    <LanguageSelector />
                  </div>
                </div>

                {/* Switch to Driver View for admin-driver */}
                {isAdminDriver && (
                  <button
                    onClick={() => {
                      navigate('/driver');
                      setMoreOpen(false);
                    }}
                    className="w-full flex items-center gap-3 p-4 mb-4 bg-emerald-50 text-emerald-700 rounded-xl font-medium"
                    data-testid="mobile-switch-driver"
                  >
                    <ArrowLeftRight className="h-5 w-5" />
                    Switch to Driver View
                  </button>
                )}

                {/* Secondary Navigation Items */}
                <div className="grid grid-cols-3 gap-3 mb-6">
                  {secondaryItems.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.path);
                    return (
                      <button
                        key={item.path}
                        onClick={() => handleNavigation(item.path)}
                        className={`flex flex-col items-center justify-center p-4 rounded-xl transition-colors ${
                          active
                            ? 'bg-sky-100 text-sky-700'
                            : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                        }`}
                        data-testid={`mobile-menu-${item.label.toLowerCase()}`}
                      >
                        <Icon className="h-6 w-6 mb-2" />
                        <span className="text-xs font-medium">{item.label}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Logout Button */}
                <button
                  onClick={() => {
                    logout();
                    setMoreOpen(false);
                  }}
                  className="w-full flex items-center justify-center gap-2 p-4 text-red-600 bg-red-50 rounded-xl font-medium mb-8"
                  data-testid="mobile-logout"
                >
                  <LogOut className="h-5 w-5" />
                  Logout
                </button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>

      {/* Spacer to prevent content from being hidden behind the bottom nav */}
      <div className="lg:hidden h-16 safe-area-bottom" />
    </>
  );
}
